import { createListCollection, Portal, Select } from '@chakra-ui/react'
import { mdiChevronDown, mdiChevronUp } from '@mdi/js'
import Icon from '@mdi/react'
import { useAtom } from 'jotai'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@/components/ui/box'
import { Button } from '@/components/ui/button'
import { MenuContent, MenuItem, MenuRoot, MenuTrigger } from '@/components/ui/menu'
import { Text } from '@/components/ui/typography'
import { currentEnv } from '@/env/vars'
import useColorModeValue from '@/hooks/useColorModeValue'
import useCustomMediaQuery from '@/hooks/useCustomMediaQuery'
import { questionActionAtom } from '@/store/translate'
import { IStructuredAction } from '@/types/gigiChat/Conversation'
import { IConversationSettings } from '@/types/gigiChat/ConversationSettings'

import StarRating from '../liveAgent/rating'

const CallToAction = ({
    structuredActions,
    settings,
    onNewQuestion,
    question,
}: {
    structuredActions?: Array<IStructuredAction>
    settings: IConversationSettings | null
    onNewQuestion?: (question: string) => void
    question?: string
}) => {
    const [, setQuestionAction] = useAtom(questionActionAtom)
    const { t } = useTranslation()
    const [itemIndex, setItemIndex] = useState<number>(0)
    const [isOpen, setIsOpen] = useState<boolean>(false)
    const [isMobile] = useCustomMediaQuery()
    const containerBackroundColor = useColorModeValue('rgba(84, 79, 64, 0.08)', 'var(--chakra-colors-dark-100)')
    const buttonBackgroundColor = useColorModeValue('white', 'black')
    const [_width, setWidth] = useState(250)
    const buttonElementRef = useRef<HTMLButtonElement>(null)

    const updateWidth = () => {
        if (buttonElementRef.current) {
            const buttonElementWidth = buttonElementRef.current.offsetWidth
            setWidth(buttonElementWidth)
        }
    }

    useEffect(() => {
        updateWidth()
        window.addEventListener('resize', updateWidth)

        return () => {
            window.removeEventListener('resize', updateWidth)
        }
    }, [])

    if (!currentEnv.features.isEnabled('connectToLiveAgent') && structuredActions && structuredActions[itemIndex].source === 'gigi') {
        return null
    }

    const isAllGigiSource = () => {
        return structuredActions && structuredActions.every((item) => item.source === 'gigi')
    }

    const isAllRatingSource = () => {
        return structuredActions && structuredActions.every((item) => item.source === 'rating5stars')
    }

    const groupedActions = (structuredActions ?? []).reduce<{
        buttons: IStructuredAction[]
        selects: Record<string, IStructuredAction[]>
    }>(
        (acc, item) => {
            const groupLabel = item.gigiExtendedAttributes?.groupableLabel || ''
            if (!groupLabel) {
                acc.buttons.push(item)
            } else {
                if (!acc.selects[groupLabel]) {
                    acc.selects[groupLabel] = []
                }
                acc.selects[groupLabel].push(item)
            }
            return acc
        },
        { buttons: [], selects: {} },
    )

    const generateOptions = () => {
        const items = groupedActions.selects
        return Object.keys(items).flatMap((option) =>
            items[option].map((item) => ({
                value: option,
                label: item.gigiExtendedAttributes?.label ?? item.name,
                name: item.name,
            })),
        )
    }

    const collection = createListCollection({ items: generateOptions() })

    if (structuredActions && structuredActions.length > 1 && isAllGigiSource()) {
        return (
            <Box mb="8px" mt="8px" display="flex" flexWrap="wrap" alignItems="center">
                {/* Render buttons for items without groupableLabel */}
                {groupedActions.buttons.map((item, index) => (
                    <Box
                        display="inline-flex"
                        borderRadius="8px"
                        marginLeft="8px"
                        marginTop="8px"
                        style={{
                            border: '1px solid #D8D7D5',
                            background: '#FFF',
                        }}
                        key={item.name + index}
                    >
                        <Button
                            variant="secondary"
                            background={buttonBackgroundColor}
                            pr="12px"
                            pl="12px"
                            pt="8px"
                            pb="8px"
                            width={isMobile ? '100%' : 'auto'}
                            onClick={() => {
                                if (onNewQuestion && question && settings && structuredActions) {
                                    setQuestionAction('QCallToAction')
                                    onNewQuestion(item.name)
                                }
                            }}
                        >
                            <Text letterSpacing="-0.32px" fontSize="16px" fontWeight="400">
                                {item.gigiExtendedAttributes?.label || item.name}
                            </Text>
                        </Button>
                    </Box>
                ))}

                {/* Render selects for grouped items */}
                {Object.entries(groupedActions.selects).map(([groupLabel], index) => (
                    <Box key={groupLabel + index} ml="8px" mt="8px" width="320px">
                        <Select.Root collection={collection} size="sm" width="100%" multiple={false}>
                            <Select.HiddenSelect
                                style={{
                                    display: 'none',
                                }}
                            />
                            <Select.Control>
                                <Select.Trigger
                                    pr="12px"
                                    pl="12px"
                                    pt="8px"
                                    pb="8px"
                                    borderRadius="8px"
                                    cursor="pointer"
                                    style={{
                                        border: '1px solid #000',
                                    }}
                                >
                                    <Select.ValueText
                                        placeholder={groupLabel}
                                        color="black"
                                        letterSpacing="-0.32px"
                                        fontSize="16px"
                                        fontWeight="400"
                                    />
                                </Select.Trigger>
                                <Select.IndicatorGroup>
                                    <Select.Indicator />
                                </Select.IndicatorGroup>
                            </Select.Control>
                            <Portal>
                                <Select.Positioner css={{ width: 'var(--reference-width)' }}>
                                    <Select.Content>
                                        {collection.items.map((item) => (
                                            <Select.Item
                                                item={item}
                                                key={item.value}
                                                onClick={() => {
                                                    if (onNewQuestion && question && settings && structuredActions) {
                                                        setQuestionAction('QCallToAction')
                                                        onNewQuestion(item.name)
                                                    }
                                                }}
                                                cursor="pointer"
                                                css={{
                                                    '&:hover': {
                                                        backgroundColor: 'var(--chakra-colors-gray-100)',
                                                    },
                                                    '&[data-scope]': {
                                                        backgroundColor: 'white',
                                                    },
                                                }}
                                            >
                                                {item.label}
                                                <Select.ItemIndicator />
                                            </Select.Item>
                                        ))}
                                    </Select.Content>
                                </Select.Positioner>
                            </Portal>
                        </Select.Root>
                    </Box>
                ))}
            </Box>
        )
    }

    if (structuredActions && structuredActions.length > 1 && isAllRatingSource()) {
        const names: Array<string> = structuredActions.map((action) => action.name)
        return <StarRating values={names} onRatingClick={onNewQuestion} />
    }

    if (structuredActions && structuredActions.length === 1) {
        return structuredActions.map((item, index) => {
            return (
                <Box background={containerBackroundColor} pl="16px" pb="12px" pt="8px" display="flex" key={item.name + index}>
                    <Button
                        variant="secondary"
                        background={buttonBackgroundColor}
                        pr="12px"
                        pl="12px"
                        pt="8px"
                        pb="8px"
                        width={isMobile ? '100%' : 'auto'}
                        mr={isMobile ? '18px' : '0'}
                        onClick={() => {
                            if (onNewQuestion && question && settings && structuredActions && item.source === 'gigi') {
                                setQuestionAction('QCallToAction')
                                onNewQuestion(item.name)
                            } else if (item) {
                                window.open(item.url, '_blank')
                            }
                        }}
                        key={item.name + index}
                    >
                        <Text letterSpacing="1.5px" fontSize="12px" textTransform="uppercase">
                            {item.name}
                        </Text>
                    </Button>
                </Box>
            )
        })
    }

    return (
        <Box
            background={containerBackroundColor}
            pl="16px"
            pb="12px"
            pt="8px"
            pr={isMobile ? '16px' : '0'}
            display="flex"
            flexWrap="wrap"
            flexDirection={isMobile ? 'column' : 'row'}
            position="relative"
            width="100%"
        >
            <MenuRoot variant="export" onOpenChange={() => setIsOpen(!isOpen)} positioning={{ placement: 'top-start' }}>
                <MenuTrigger asChild>
                    <Button
                        as={Button}
                        pr="12px"
                        pl="12px"
                        pt="8px"
                        pb="8px"
                        borderRadius="8px"
                        width={isMobile ? '100%' : 'auto'}
                        onClick={() => setIsOpen(!isOpen)}
                        ref={buttonElementRef}
                    >
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Text
                                marginRight="4px"
                                as="span"
                                overflow="hidden"
                                whiteSpace="nowrap"
                                textOverflow="ellipsis"
                                width="calc(100% - 8px)"
                            >
                                {structuredActions && structuredActions[itemIndex].name}
                            </Text>
                            <Icon path={isOpen ? mdiChevronUp : mdiChevronDown} size="24px" />
                        </Box>
                    </Button>
                </MenuTrigger>
                <MenuContent maxW="calc(100vw - 20px)">
                    {structuredActions &&
                        structuredActions.map((item, index) => {
                            return (
                                <MenuItem value={item.name} key={item.name} onClick={() => setItemIndex(index)}>
                                    <Text fontSize="14px" letterSpacing="0.25px" truncate as="span">
                                        {item.gigiExtendedAttributes?.label ?? item.name}
                                    </Text>
                                </MenuItem>
                            )
                        })}
                </MenuContent>
            </MenuRoot>
            <Button
                variant="secondary"
                background={buttonBackgroundColor}
                pr="12px"
                pl="12px"
                pt="8px"
                pb="8px"
                ml={isMobile ? '0' : '24px'}
                mt={isMobile ? '16px' : '0'}
                onClick={() => {
                    if (
                        onNewQuestion &&
                        question &&
                        settings &&
                        structuredActions &&
                        ['gigi', 'rating5stars'].includes(structuredActions[itemIndex].source)
                    ) {
                        setQuestionAction('QCallToAction')
                        onNewQuestion(structuredActions[itemIndex].name)
                    } else if (structuredActions) {
                        window.open(structuredActions[itemIndex].url, '_blank')
                    }
                }}
            >
                <Text letterSpacing="0.25px">{t('chat.callToAction.title')}</Text>
            </Button>
        </Box>
    )
}

export default CallToAction
